import { Alert, Badge, Button, Group, Loader, Modal, Paper, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import {
  useDeleteProjectsAutodeskConnectionMutation,
  useGetProjectsAutodeskConnectionQuery,
  usePostProjectsAutodeskVerifyServiceAccountAccessMutation,
} from "../../../../../openapi";
import { useAppSelector } from "../../../../hooks";
import { ConnectAutodeskProjectModal } from "./ConnectAutodeskProjectModal";

export const Bim360 = () => {
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const project = useAppSelector((state) => state.adminReducer.project);
  const projectId = project?._id;

  const { data, isFetching, isError, refetch } = useGetProjectsAutodeskConnectionQuery(
    { projectId },
    { skip: projectId == null },
  );
  const [
    verifyServiceAccountAccess,
    { isLoading: isVerifyingServiceAccountAccess, isError: verifyServiceAccountAccessFailed },
  ] = usePostProjectsAutodeskVerifyServiceAccountAccessMutation();
  const [
    disconnectAutodeskProject,
    { isLoading: isDisconnectingAutodeskProject, isError: disconnectAutodeskProjectFailed },
  ] = useDeleteProjectsAutodeskConnectionMutation();

  const bim360 = data?.bim360;
  const serviceAccountAccess = bim360?.automation?.serviceAccountAccess ?? "unknown";

  return (
    <Paper withBorder radius="sm" p="lg">
      <Stack gap="md">
        <div>
          <Title order={3}>Autodesk Forma</Title>
          <Text c="dimmed" size="sm">
            Koble Forma-prosjektet og kontroller at MCApps kan lese filene.
          </Text>
        </div>

        {isFetching && <Loader size="sm" />}

        {isError && <Alert color="danger">Kunne ikke hente Autodesk-koblingen for prosjektet.</Alert>}
        {verifyServiceAccountAccessFailed && <Alert color="red">Kunne ikke verifisere lesetilgangen.</Alert>}
        {disconnectAutodeskProjectFailed && <Alert color="red">Kunne ikke koble fra Autodesk-prosjektet.</Alert>}

        {!isFetching && !isError && bim360 == null && (
          <Stack gap="sm">
            <Text>Dette MCApps-prosjektet er ikke koblet til et Autodesk-prosjekt.</Text>
            <Group>
              <Button color="blue" onClick={() => setConnectModalOpen(true)}>
                Koble Autodesk-prosjekt
              </Button>
            </Group>
          </Stack>
        )}

        {!isFetching && !isError && bim360 != null && (
          <Stack gap="md">
            <Stack gap={4}>
              <Text size="sm" fw={700}>
                Prosjekt
              </Text>
              <Text>{bim360.name}</Text>
            </Stack>
            <Group gap="sm">
              <Text size="sm" fw={700}>
                Filtilgang
              </Text>
              <Badge color={getServiceAccountAccessColor(serviceAccountAccess)} variant="light">
                {formatServiceAccountAccess(serviceAccountAccess)}
              </Badge>
            </Group>
            {serviceAccountAccess === "failed" && bim360.automation?.lastError != null && (
              <Alert color="yellow">{bim360.automation.lastError}</Alert>
            )}
            <Group>
              <Button color="blue" onClick={() => setConnectModalOpen(true)}>
                Koble Autodesk-prosjekt
              </Button>
              <Button
                variant="outline"
                disabled={projectId == null}
                loading={isVerifyingServiceAccountAccess}
                onClick={async () => {
                  if (projectId == null) {
                    return;
                  }

                  await verifyServiceAccountAccess({ body: { projectId } }).unwrap();
                  refetch();
                }}
              >
                Verifiser lesetilgang
              </Button>
              <Button
                color="red"
                variant="outline"
                disabled={projectId == null}
                loading={isDisconnectingAutodeskProject}
                onClick={() => setDisconnectModalOpen(true)}
              >
                Koble fra
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
      <ConnectAutodeskProjectModal
        opened={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onConnected={() => {
          refetch();
          setConnectModalOpen(false);
        }}
        projectId={projectId}
      />
      <Modal opened={disconnectModalOpen} onClose={() => setDisconnectModalOpen(false)} title="Koble fra Autodesk Forma">
        <Stack gap="md">
          <Text>
            Er du sikker på at du vil koble fra {bim360?.name ?? "Autodesk-prosjektet"}? MCApps mister koblingen til
            Forma-prosjektet, men filer og prosjektet i Autodesk påvirkes ikke.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDisconnectModalOpen(false)}>
              Avbryt
            </Button>
            <Button
              color="red"
              loading={isDisconnectingAutodeskProject}
              onClick={async () => {
                if (projectId == null) {
                  return;
                }

                await disconnectAutodeskProject({ projectId }).unwrap();
                setDisconnectModalOpen(false);
                refetch();
              }}
            >
              Koble fra
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};

export default Bim360;

export const Icon = ({ isValid }: { isValid: boolean }) => {
  return isValid ? (
    <i style={{ color: "green" }} className="fa fa-check-circle fa-2x fa-fw" />
  ) : (
    <i style={{ color: "red" }} className="fa fa-close fa-fw" />
  );
};

const formatServiceAccountAccess = (status: "unknown" | "verified" | "failed") => {
  switch (status) {
    case "verified":
      return "Lesetilgang verifisert";
    case "failed":
      return "Mangler lesetilgang";
    default:
      return "Ikke verifisert";
  }
};

const getServiceAccountAccessColor = (status: "unknown" | "verified" | "failed") => {
  switch (status) {
    case "verified":
      return "green";
    case "failed":
      return "red";
    default:
      return "yellow";
  }
};
